import { Component, EventEmitter, Output, Input, OnInit, OnDestroy, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgbDropdownModule, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { form, FormField, required, validate } from '@angular/forms/signals';
import { AttendenceService } from '../../shared/services/attendence.service';
import { AuthService } from '../../shared/services/auth.service';
import { OpenSnackBarService } from '../../shared/services/snackBar.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { BarcodeFormat } from '@zxing/library';
import { DataService } from '../../shared/services/data.service';
import { GlobalSearchComponent } from './global-search/global-search.component';

interface ChangePasswordModel {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

// Text fields start as '' -- a null initial value logs NG01921 and the control
// never writes back into the model.
function emptyChangePasswordModel(): ChangePasswordModel {
  return { old_password: '', new_password: '', confirm_password: '' };
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: true,
    imports: [CommonModule, NgbDropdownModule, MatFormFieldModule, MatInputModule, ZXingScannerModule, FormsModule, FormField, GlobalSearchComponent]
})
export class HeaderComponent implements OnInit, OnDestroy {
  checkInModal: NgbModalRef = {} as NgbModalRef;
  changePasswordModal: NgbModalRef;
  checkedInUser: any;
  wayToCheckIn = signal<any>('manual');
  checkedInCode = signal<any>(undefined);
  scannedCodeOnce: number = 0;

  // Camera properties
  hasCameraPermission = signal<boolean>(false);
  cameraAvailable = signal<boolean>(false);
  scannerEnabled = signal<boolean>(false);
  availableDevices: MediaDeviceInfo[] = [];
  currentDevice: MediaDeviceInfo | undefined;
  allowedFormats = [BarcodeFormat.QR_CODE, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX];

  userType = signal<any>('');
  changePasswordModel = signal<ChangePasswordModel>(emptyChangePasswordModel());
  // Signal Forms. `form()` needs an injection context, so it is a field
  // initializer -- specs must build the component through TestBed.
  changePasswordForm = form(this.changePasswordModel, (p) => {
    required(p.old_password, { message: 'Old password is required' });
    required(p.new_password, { message: 'New password is required' });
    required(p.confirm_password, { message: 'Confirm password is required' });
    // This check did not exist before the signals migration: the old FormGroup
    // had no cross-field validator, so a mismatched confirmation could be
    // submitted. Mirrors client-profile-v3's reset-password form.
    validate(p.confirm_password, ({ value, valueOf }) => {
      const confirm = value();
      const newPassword = valueOf(p.new_password);
      return newPassword && confirm && newPassword !== confirm ? { kind: 'mismatch' } : null;
    });
  });
  changePasswordLoadingFlag = signal<boolean>(false);
  @Input()
  heading: string = '';
  canCheckIn = signal<boolean>(false);
  constructor(private router: Router,
    private attendenceService: AttendenceService,
    private authService: AuthService,
    private modal: NgbModal,
    private openSnackBarService: OpenSnackBarService,
    private dataService: DataService) {

    this.userType.set(localStorage.getItem("auth_role"));
    // console.log(this.userType());
  }
  
  ngOnInit(): void {
    this.canCheckIn.set(this.dataService.hasPermission('client-management','client-profile', 'view'));
    // Simple check for camera availability
    this.checkCameraAvailability();
  }
  
  ngOnDestroy(): void {
    if (this.checkInModal && this.checkInModal.close) {
      this.checkInModal.close();
    }
    if (this.changePasswordModal && this.changePasswordModal.close) {
      this.changePasswordModal.close();
    }
  }
  
  /**
   * Checks if the device has camera capabilities
   */
  checkCameraAvailability(): void {
    // Simply check if the API exists without requesting permission yet
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      this.cameraAvailable.set(true);
      // Don't request permission until user selects camera option
    } else {
      this.cameraAvailable.set(false);
      this.hasCameraPermission.set(false);
      console.warn('Camera access is not supported in this browser or device.');
    }
  }
  
  /**
   * Handler for camera permission events
   */
  onHasPermission(has: boolean): void {
    this.hasCameraPermission.set(has);
    if (!has) {
      console.warn('Camera permission denied or not available.');
      // Don't force manual mode immediately
    }
  }
  
  /**
   * Handler for device change events
   */
  onCamerasFound(devices: MediaDeviceInfo[]): void {
    this.availableDevices = devices;
    if (devices && devices.length > 0) {
      this.currentDevice = devices[0];
    }
  }
  
  /**
   * Handler for camera errors
   */
  onCameraError(error: Error): void {
    console.error('Camera error:', error);
    this.openSnackBarService.openSnackBar('fail', 'Camera could not be accessed. Please try manual check-in.');
    this.wayToCheckIn.set('manual');
  }
  @Output()
  toggleSidenav = new EventEmitter<void>();
  logout() {
    this.authService.performLogout();
  }
  toUserProfile() {
    this.router.navigateByUrl('/user-profile');
  }
  openCheckInModal(checkInModal :  any) {
    // Reset scanner state and check-in code before opening modal
    this.wayToCheckIn.set('manual');
    this.scannerEnabled.set(false);
    this.checkedInCode.set('');
    this.scannedCodeOnce = 0;
    
    // Open the modal
    this.checkInModal = this.modal.open(checkInModal, { size: 'lg' });
    
    // Check camera availability again when modal opens
    this.checkCameraAvailability();
  }
  checkInWay(way : any) {
    this.wayToCheckIn.set(way);
    if (way === 'camera') {
      if (!this.cameraAvailable()) {
        this.openSnackBarService.openSnackBar('fail', 'Camera is not available on this device or browser.');
        this.wayToCheckIn.set('manual');
        return;
      }
      
      // Simply enable the scanner and let the component handle permissions
      this.scannerEnabled.set(true);
    } else {
      this.scannerEnabled.set(false);
    }
  }
  CheckInUser() {
    // console.log(this.checkedInCode());
    
    this.attendenceService.checkInUser(this.checkedInCode()).subscribe(
      (res: any) => {
        // console.log(res)
        this.checkInModal.close();
        this.router.navigateByUrl(`${this.userType() == "checkin" ? 'checkin' : ''}/client-management/client-profile-v3/code/${res.code}`);
        this.checkedInCode.set("");
      },
      (err: any) => {
        this.openSnackBarService.openSnackBar('fail', err.error);
      }
    );
  }
  integrateWithOutlook() {
    this.authService.integrateWithOutlook().subscribe(
      (res: any) => {
        // console.log(res);

        window.open(res ,"_target");
      }, err => {
        this.openSnackBarService.openSnackBar('fail', err.error);
      }
    );
  }
  scanSuccess(value :  any) {
    this.scannedCodeOnce++;
    if (this.scannedCodeOnce == 1) {
      this.attendenceService.checkInUser(value).subscribe(
        (res: any) => {
          // console.log(res)
          this.checkInModal.close();
          this.router.navigateByUrl(`${this.userType() == "checkin" ? 'checkin' : ''}/client-management/client-profile-v3/code/${res.code}`);
          this.scannedCodeOnce = 0
        },
        (err: any) => {
          this.openSnackBarService.openSnackBar('fail', err.error);
        }
      );
    }

  }
  /**
   * Explicitly request camera permission
   */
  requestCameraPermission(): void {
    // Simply enable the scanner - it will handle permission requests
    this.scannerEnabled.set(true);
  }
  
  closeCheckInModal() {
    this.checkInModal.close();
  }
  changePassword(changePasswordModal : any) {
    this.changePasswordForm().reset(emptyChangePasswordModel());
    this.changePasswordModal = this.modal.open(changePasswordModal, { size: 'lg' });
  }
  closeChangePasswordModal() {
    this.changePasswordModal.close();
  }
  changePasswordTouched(field: keyof ChangePasswordModel): boolean {
    return this.changePasswordForm[field]().touched();
  }

  changePasswordHasError(field: keyof ChangePasswordModel, kind: string): boolean {
    return this.changePasswordForm[field]().errors().some(e => e.kind === kind);
  }

  changePasswordSubmit() {
    if (this.changePasswordForm().invalid()) return;
    let body: any = { ...this.changePasswordModel() };
    this.changePasswordLoadingFlag.set(true);
    body["_method"] = "PUT"

    this.authService.updateUserPassword(body).subscribe(res => {

      this.changePasswordForm().reset(emptyChangePasswordModel());
      this.changePasswordLoadingFlag.set(false);
      this.closeChangePasswordModal();
      this.openSnackBarService.openSnackBar('success', "Password successfully changed");
    },
      err => {
        this.changePasswordLoadingFlag.set(false);
        this.openSnackBarService.openSnackBar('fail', err.error);
      });
  }
}
